import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Center, CenterDaySchedule } from '@api/types';
import { adminCentersApi, adminTagsApi, ApiRequestError, type CenterAdmin, type Tag } from '@api/client';
import { useAuth } from '@hooks/useAuth';
import { useConfirm } from '@hooks/useConfirm';
import * as Icons from '@components/Icons';
import { LifecycleFields } from './LifecycleFields';
import { CenterDrawerSalasTab } from './CenterDrawerSalasTab';
import { CenterDrawerIntegracionesTab } from './CenterDrawerIntegracionesTab';

// ---------------------------------------------------------------------------
// Business hours helpers (display order Mon→Sun mapped to internal 0=Sun array)
// ---------------------------------------------------------------------------
const DISPLAY_TO_WEEKDAY = [1, 2, 3, 4, 5, 6, 0] as const;
const DAY_NAMES = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

interface DayState { closed: boolean; open: string; close: string }

const DEFAULT_BH: DayState[] = [
  { closed: false, open: '09:00', close: '20:00' },
  { closed: false, open: '09:00', close: '20:00' },
  { closed: false, open: '09:00', close: '20:00' },
  { closed: false, open: '09:00', close: '20:00' },
  { closed: false, open: '09:00', close: '20:00' },
  { closed: true,  open: '09:00', close: '14:00' },
  { closed: true,  open: '09:00', close: '14:00' },
];

function bhFromApi(bh: (CenterDaySchedule | null)[] | null | undefined): DayState[] {
  if (bh?.length !== 7) return DEFAULT_BH.map((d) => ({ ...d }));
  return DISPLAY_TO_WEEKDAY.map((wd) => {
    const entry = bh[wd];
    return entry ? { closed: false, open: entry.open, close: entry.close } : { closed: true, open: '09:00', close: '14:00' };
  });
}

function bhToApi(days: DayState[]): (CenterDaySchedule | null)[] {
  const result: (CenterDaySchedule | null)[] = new Array(7).fill(null) as (CenterDaySchedule | null)[];
  DISPLAY_TO_WEEKDAY.forEach((wd, i) => {
    const d = days[i];
    result[wd] = d.closed ? null : { open: d.open, close: d.close };
  });
  return result;
}

const TIMEZONE_OPTIONS = [
  { group: 'España', options: [
    ['Europe/Madrid', 'Europe/Madrid (Península y Baleares)'],
    ['Atlantic/Canary', 'Atlantic/Canary (Canarias)'],
  ]},
  { group: 'Europa', options: [
    ['Europe/London', 'Europe/London'],
    ['Europe/Paris', 'Europe/Paris'],
    ['Europe/Berlin', 'Europe/Berlin'],
    ['Europe/Rome', 'Europe/Rome'],
    ['Europe/Lisbon', 'Europe/Lisbon'],
    ['Europe/Amsterdam', 'Europe/Amsterdam'],
    ['Europe/Brussels', 'Europe/Brussels'],
    ['Europe/Zurich', 'Europe/Zurich'],
    ['Europe/Warsaw', 'Europe/Warsaw'],
    ['Europe/Moscow', 'Europe/Moscow'],
  ]},
  { group: 'América', options: [
    ['America/Mexico_City', 'America/Mexico_City'],
    ['America/Bogota', 'America/Bogota'],
    ['America/Lima', 'America/Lima'],
    ['America/Santiago', 'America/Santiago'],
    ['America/Argentina/Buenos_Aires', 'America/Argentina/Buenos_Aires'],
    ['America/New_York', 'America/New_York'],
    ['America/Chicago', 'America/Chicago'],
    ['America/Los_Angeles', 'America/Los_Angeles'],
    ['America/Sao_Paulo', 'America/Sao_Paulo'],
  ]},
  { group: 'Resto del mundo', options: [
    ['Asia/Dubai', 'Asia/Dubai'],
    ['Asia/Kolkata', 'Asia/Kolkata'],
    ['Asia/Singapore', 'Asia/Singapore'],
    ['Asia/Tokyo', 'Asia/Tokyo'],
    ['Australia/Sydney', 'Australia/Sydney'],
    ['UTC', 'UTC'],
  ]},
];

// CenterCreateModal vive en su propio fichero. Re-exportado para no romper
// los imports que ya hacen `from './CenterDrawer'`.
export { CenterCreateModal } from './CenterCreateModal';

// ---------------------------------------------------------------------------
// Edit drawer — slides from the right, tabs for Identidad / Contacto / Operación
// ---------------------------------------------------------------------------
type TabKey = 'identidad' | 'contacto' | 'tags' | 'administradores' | 'salas' | 'integraciones' | 'operacion';

interface CenterDrawerProps {
  center: Center;
  onClose: () => void;
  onSave: (data: Record<string, unknown>) => Promise<void>;
  isSaving: boolean;
  /** When provided, surface a danger "Eliminar" button in the footer. The
   *  parent owns the confirmation flow. */
  onDelete?: () => void;
}

export function CenterDrawer({ center, onClose, onSave, isSaving, onDelete }: CenterDrawerProps) {
  const { hasRole } = useAuth();
  const isSuper = hasRole('superadmin');
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [tab, setTab] = useState<TabKey>('identidad');

  // Identidad
  const [name, setName] = useState(center.name);
  const [timezone, setTimezone] = useState(center.timezone);
  const [isActive, setIsActive] = useState<boolean>(center.isActive ?? true);

  // Público: tags catalog + selected tagIds + primary admin
  const { data: tagsData } = useQuery<{ items: Tag[] }>({
    queryKey: ['tags', 'list'],
    queryFn: () => adminTagsApi.list(),
  });
  const { data: declaredTagsData } = useQuery<{ items: string[] }>({
    queryKey: ['center', center.id, 'tags'],
    queryFn: () => adminCentersApi.listTagIds(center.id),
  });
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set());
  const [tagIdsTouched, setTagIdsTouched] = useState(false);
  // Sync once when the declared set loads.
  if (declaredTagsData && !tagIdsTouched && selectedTagIds.size === 0 && declaredTagsData.items.length > 0) {
    setSelectedTagIds(new Set(declaredTagsData.items));
  }
  const [primaryAdminUserId, setPrimaryAdminUserId] = useState<string | null>(
    center.primaryAdminUserId ?? null,
  );

  // Administradores: list current admins (super only).
  const { data: adminsData, refetch: refetchAdmins } = useQuery<CenterAdmin[]>({
    queryKey: ['center', center.id, 'admins'],
    queryFn: () => adminCentersApi.listAdmins(center.id),
    enabled: isSuper,
  });
  const admins = adminsData ?? [];
  const [grantEmailInput, setGrantEmailInput] = useState('');
  const [grantError, setGrantError] = useState<string | null>(null);

  const grantByUserIdMutation = useMutation({
    mutationFn: (userId: string) => adminCentersApi.grantAdmin(center.id, userId),
    onSuccess: () => {
      setGrantEmailInput('');
      setGrantError(null);
      void refetchAdmins();
      void qc.invalidateQueries({ queryKey: ['center', center.id, 'admins'] });
    },
    onError: () => { setGrantError('No se pudo otorgar admin. ¿Es un userId válido?'); },
  });

  const revokeMutation = useMutation({
    mutationFn: (userId: string) => adminCentersApi.revokeAdmin(center.id, userId),
    onSuccess: () => {
      void refetchAdmins();
      void qc.invalidateQueries({ queryKey: ['center', center.id, 'admins'] });
    },
  });

  // Crear nueva tag desde dentro del tab Tags (solo superadmin).
  const [newTagName, setNewTagName] = useState('');
  const [tagCreateErr, setTagCreateErr] = useState<string | null>(null);
  const createTagMutation = useMutation({
    mutationFn: (name: string) => adminTagsApi.create(name),
    onSuccess: (created) => {
      setNewTagName('');
      setTagCreateErr(null);
      void qc.invalidateQueries({ queryKey: ['tags', 'list'] });
      // Auto-marcamos la recién creada en el centro: el comportamiento esperado
      // de "crear y añadir" desde el centro.
      setTagIdsTouched(true);
      setSelectedTagIds((prev) => new Set(prev).add(created.id));
    },
    onError: (e) => {
      if (e instanceof ApiRequestError && e.status === 409) {
        setTagCreateErr('Ya existe una tag con ese nombre.');
      } else {
        setTagCreateErr('No se pudo crear la tag.');
      }
    },
  });

  const deleteTagMutation = useMutation({
    mutationFn: (id: string) => adminTagsApi.delete(id),
    onSuccess: (_void, deletedId) => {
      void qc.invalidateQueries({ queryKey: ['tags', 'list'] });
      // Si estaba seleccionada para este centro, la quitamos del estado local.
      setSelectedTagIds((prev) => {
        if (!prev.has(deletedId)) return prev;
        const next = new Set(prev);
        next.delete(deletedId);
        return next;
      });
    },
  });

  async function handleDeleteTag(tag: Tag) {
    const ok = await confirm({
      title: `¿Eliminar la tag "${tag.name}" del catálogo?`,
      message:
        'Esto la quita de TODOS los centros que la tengan declarada y la borra del catálogo global. Acción no reversible.',
      confirmLabel: 'Eliminar',
      tone: 'danger',
    });
    if (!ok) return;
    deleteTagMutation.mutate(tag.id);
  }

  // Contacto
  const [address, setAddress] = useState(center.address ?? '');
  const [city, setCity] = useState(center.city ?? '');
  const [province, setProvince] = useState(center.province ?? '');
  const [contactEmail, setContactEmail] = useState(center.contactEmail ?? '');
  const [contactPhone, setContactPhone] = useState(center.contactPhone ?? '');

  // Operación
  const [allowedOrigins, setAllowedOrigins] = useState(
    Array.isArray(center.allowedOrigins) ? center.allowedOrigins.join('\n') : '',
  );
  const [confirmationMode, setConfirmationMode] = useState<'auto' | 'manual'>(center.confirmationMode ?? 'auto');
  const [bhEnabled, setBhEnabled] = useState(Array.isArray(center.businessHours));
  const [bhDays, setBhDays] = useState<DayState[]>(() => bhFromApi(center.businessHours));
  const [pendingTtlHours, setPendingTtlHours] = useState<number>(center.pendingTtlHours ?? 24);
  const [approvalTtlHours, setApprovalTtlHours] = useState<number>(center.approvalTtlHours ?? 72);
  const [checkinCompletionBufferMinutes, setCheckinCompletionBufferMinutes] = useState<number>(
    center.checkinCompletionBufferMinutes ?? 30,
  );

  const [err, setErr] = useState('');

  function toOrigins(raw: string) {
    return raw.split('\n').map((s) => s.trim()).filter(Boolean);
  }

  async function handleSubmit(e?: React.SyntheticEvent) {
    e?.preventDefault();
    setErr('');
    if (!name.trim()) { setErr('El nombre es obligatorio.'); setTab('identidad'); return; }
    if (!timezone) { setErr('La zona horaria es obligatoria.'); setTab('identidad'); return; }
    try {
      await onSave({
        name: name.trim(),
        timezone,
        isActive,
        address: address.trim() === '' ? null : address.trim(),
        city: city.trim() === '' ? null : city.trim(),
        province: province.trim() === '' ? null : province.trim(),
        contactEmail: contactEmail.trim() === '' ? null : contactEmail.trim(),
        contactPhone: contactPhone.trim() === '' ? null : contactPhone.trim(),
        allowedOrigins: toOrigins(allowedOrigins),
        confirmationMode,
        businessHours: bhEnabled ? bhToApi(bhDays) : null,
        pendingTtlHours,
        approvalTtlHours,
        checkinCompletionBufferMinutes,
        tagIds: Array.from(selectedTagIds),
        primaryAdminUserId,
      });
    } catch (e) {
      const msg = e instanceof Error && e.message ? e.message : 'Error al guardar. Inténtalo de nuevo.';
      setErr(msg);
    }
  }

  return (
    <div className="modal-scrim" data-variant="drawer" onClick={onClose}>
      <div className="modal" onClick={(e) => { e.stopPropagation(); }}>
        <div className="modal-head">
          <div className="modal-title">Editar centro · {center.name}</div>
          <button className="icon-btn" data-variant="ghost" onClick={onClose}><Icons.X size={14} /></button>
        </div>
        <div style={{ padding: '0 18px 12px', borderBottom: '1px solid var(--border)' }}>
          <div className="seg">
            <button
              type="button"
              className="seg-btn"
              data-active={tab === 'identidad'}
              onClick={() => { setTab('identidad'); }}
            >
              Identidad
            </button>
            <button
              type="button"
              className="seg-btn"
              data-active={tab === 'contacto'}
              onClick={() => { setTab('contacto'); }}
            >
              Contacto
            </button>
            <button
              type="button"
              className="seg-btn"
              data-active={tab === 'tags'}
              onClick={() => { setTab('tags'); }}
            >
              Tags
            </button>
            {isSuper && (
              <button
                type="button"
                className="seg-btn"
                data-active={tab === 'administradores'}
                onClick={() => { setTab('administradores'); }}
              >
                Administradores
              </button>
            )}
            <button
              type="button"
              className="seg-btn"
              data-active={tab === 'salas'}
              onClick={() => { setTab('salas'); }}
            >
              Salas
            </button>
            <button
              type="button"
              className="seg-btn"
              data-active={tab === 'integraciones'}
              onClick={() => { setTab('integraciones'); }}
            >
              Integraciones
            </button>
            <button
              type="button"
              className="seg-btn"
              data-active={tab === 'operacion'}
              onClick={() => { setTab('operacion'); }}
            >
              Operación
            </button>
          </div>
        </div>
        <form className="modal-body" onSubmit={handleSubmit}>
          {err && <FormError msg={err} />}

          {!isSuper && (
            <div
              style={{
                padding: '10px 12px',
                marginBottom: 14,
                background: 'var(--bg-soft)',
                border: '1px dashed var(--border-strong)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 12.5,
                color: 'var(--fg-muted)',
              }}
            >
              Como admin de centro, solo puedes gestionar <strong>Salas</strong>.
              El resto de los datos del centro son de solo lectura — pide a un
              superadmin si necesitas cambiar nombre, contacto, horarios u otros.
            </div>
          )}

          {tab === 'identidad' && (
            <>
              <div className="field">
                <label className="field-label">Nombre <Req /></label>
                <input className="input" value={name} onChange={(e) => { setName(e.target.value); }} autoFocus />
              </div>
              <div className="field">
                <label className="field-label">Slug (URL)</label>
                <input className="input mono" value={center.slug} readOnly />
                <p className="field-help">El slug es inmutable — define la URL pública del widget.</p>
              </div>
              <div className="field">
                <label className="field-label">Zona horaria <Req /></label>
                <TimezoneSelect value={timezone} onChange={setTimezone} />
              </div>
              <div className="field">
                <label className="field-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => { setIsActive(e.target.checked); }}
                    style={{ width: 14, height: 14, accentColor: 'var(--accent)' }}
                  />
                  Centro activo
                </label>
                <p className="field-help">
                  Si está desactivado, el centro no acepta reservas nuevas pero los datos se conservan.
                </p>
              </div>
            </>
          )}

          {tab === 'contacto' && (
            <>
              <div className="field">
                <label className="field-label">Dirección</label>
                <input
                  className="input"
                  value={address}
                  onChange={(e) => { setAddress(e.target.value); }}
                  placeholder="Calle Mayor, 12"
                />
              </div>
              <div className="field-row">
                <div className="field">
                  <label className="field-label">Ciudad</label>
                  <input
                    className="input"
                    value={city}
                    onChange={(e) => { setCity(e.target.value); }}
                    placeholder="Madrid"
                  />
                </div>
                <div className="field">
                  <label className="field-label">Provincia / región</label>
                  <input
                    className="input"
                    value={province}
                    onChange={(e) => { setProvince(e.target.value); }}
                    placeholder="Comunidad de Madrid"
                  />
                </div>
              </div>
              <div className="field">
                <label className="field-label">Email de contacto</label>
                <input
                  className="input"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => { setContactEmail(e.target.value); }}
                  placeholder="contacto@tucentro.com"
                />
                <p className="field-help">Aparecerá en emails al cliente como remitente alternativo.</p>
              </div>
              <div className="field">
                <label className="field-label">Teléfono de contacto</label>
                <input
                  className="input"
                  type="tel"
                  value={contactPhone}
                  onChange={(e) => { setContactPhone(e.target.value); }}
                  placeholder="+34 612 345 678"
                />
                <p className="field-help">Formato internacional. Se normaliza a E.164 al guardar.</p>
              </div>
            </>
          )}

          {tab === 'tags' && (
            <>
              <div className="field">
                <label className="field-label">Tags del centro</label>
                <p className="field-help" style={{ marginBottom: 8 }}>
                  Especialidades que mostrará la web pública para este centro. Marca las que apliquen.
                  {isSuper ? ' Como superadmin, puedes crear nuevas tags y eliminar tags obsoletas del catálogo global.' : ' La lista del catálogo la mantiene el superadmin.'}
                </p>
                {(tagsData?.items.length ?? 0) === 0 ? (
                  <div style={{ fontSize: 13, color: 'var(--fg-muted, #6b7280)' }}>
                    Todavía no hay tags en el catálogo. {isSuper ? 'Crea la primera abajo.' : 'Pide a un superadmin que cree alguna.'}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {(tagsData?.items ?? []).map((tag) => {
                      const checked = selectedTagIds.has(tag.id);
                      const deleting = deleteTagMutation.isPending && deleteTagMutation.variables === tag.id;
                      return (
                        <span
                          key={tag.id}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '6px 10px',
                            borderRadius: 'var(--radius-sm, 6px)',
                            border: `1px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
                            background: checked ? 'var(--accent-soft, #eef7f2)' : 'transparent',
                            fontSize: 13,
                            opacity: deleting ? 0.5 : 1,
                          }}
                        >
                          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: deleting ? 'wait' : 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={deleting}
                              onChange={(e) => {
                                setTagIdsTouched(true);
                                setSelectedTagIds((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(tag.id);
                                  else next.delete(tag.id);
                                  return next;
                                });
                              }}
                              style={{ accentColor: 'var(--accent)' }}
                            />
                            {tag.name}
                          </label>
                          {isSuper && (
                            <button
                              type="button"
                              className="icon-btn"
                              data-variant="ghost"
                              title="Eliminar tag del catálogo (afecta a todos los centros)"
                              disabled={deleting}
                              onClick={() => { void handleDeleteTag(tag); }}
                              style={{ padding: 0, width: 18, height: 18, marginLeft: 2 }}
                              aria-label={`Eliminar ${tag.name}`}
                            >
                              <Icons.X size={11} />
                            </button>
                          )}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              {isSuper && (
                <div className="field">
                  <label className="field-label">Crear una tag nueva</label>
                  <p className="field-help" style={{ marginBottom: 8 }}>
                    Se añade al catálogo global. Si la creas, la marcamos automáticamente para este centro.
                  </p>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      className="input"
                      style={{ flex: 1 }}
                      type="text"
                      maxLength={80}
                      value={newTagName}
                      onChange={(e) => { setNewTagName(e.target.value); setTagCreateErr(null); }}
                      placeholder="Ej.: Yoga terapéutico"
                    />
                    <button
                      type="button"
                      className="btn"
                      data-variant="primary"
                      disabled={createTagMutation.isPending || newTagName.trim().length < 2}
                      onClick={() => { createTagMutation.mutate(newTagName.trim()); }}
                    >
                      {createTagMutation.isPending ? 'Creando…' : 'Crear y añadir'}
                    </button>
                  </div>
                  {tagCreateErr && (
                    <p style={{ marginTop: 6, color: 'var(--danger, #b91c1c)', fontSize: 13 }}>
                      {tagCreateErr}
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {tab === 'administradores' && isSuper && (
            <>
              <div className="field">
                <label className="field-label">Admin principal del centro</label>
                <p className="field-help" style={{ marginBottom: 8 }}>
                  El nombre de este admin se muestra como "responsable" en la web pública.
                  Solo aparecen aquí los users que ya tienen permisos de admin sobre este centro.
                </p>
                {admins.length === 0 ? (
                  <div style={{ fontSize: 13, color: 'var(--fg-muted, #6b7280)' }}>
                    Este centro no tiene admins asignados. Otorga alguno abajo y vuelve a este campo.
                  </div>
                ) : (
                  <select
                    className="select"
                    value={primaryAdminUserId ?? ''}
                    onChange={(e) => { setPrimaryAdminUserId(e.target.value === '' ? null : e.target.value); }}
                  >
                    <option value="">— Sin admin asignado —</option>
                    {admins.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.email}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="field">
                <label className="field-label">Administradores del centro</label>
                <p className="field-help" style={{ marginBottom: 8 }}>
                  Cualquiera de estos usuarios puede gestionar el centro como admin. Un mismo user puede ser admin de varios centros.
                </p>
                {admins.length === 0 ? (
                  <div style={{ fontSize: 13, color: 'var(--fg-muted, #6b7280)', padding: '8px 0' }}>
                    Todavía no hay admins en este centro.
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: 6, marginBottom: 12 }}>
                    {admins.map((a) => (
                      <div
                        key={a.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '8px 10px',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm, 6px)',
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500, fontSize: 13 }}>{a.email}</div>
                          <div style={{ fontSize: 12, color: 'var(--fg-muted, #6b7280)' }}>
                            user id: <span className="mono">{a.id}</span> · rol global: {a.role}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn"
                          data-variant="danger"
                          disabled={revokeMutation.isPending}
                          onClick={() => { revokeMutation.mutate(a.id); }}
                        >
                          Revocar
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="field">
                <label className="field-label">Otorgar admin (user ID)</label>
                <p className="field-help" style={{ marginBottom: 8 }}>
                  Pega el user ID de un usuario existente. Lo obtienes desde la sección Staff editando una persona — su ficha muestra el user ID linkado.
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    className="input mono"
                    style={{ flex: 1 }}
                    type="text"
                    value={grantEmailInput}
                    onChange={(e) => {
                      setGrantEmailInput(e.target.value);
                      setGrantError(null);
                    }}
                    placeholder="01ARZ3NDEKTSV4RRFFQ69G5FAV"
                  />
                  <button
                    type="button"
                    className="btn"
                    data-variant="primary"
                    disabled={grantByUserIdMutation.isPending || grantEmailInput.trim().length < 8}
                    onClick={() => { grantByUserIdMutation.mutate(grantEmailInput.trim()); }}
                  >
                    Otorgar
                  </button>
                </div>
                {grantError && (
                  <p style={{ marginTop: 6, color: 'var(--danger, #b91c1c)', fontSize: 13 }}>
                    {grantError}
                  </p>
                )}
              </div>
            </>
          )}

          {tab === 'salas' && (
            <CenterDrawerSalasTab centerId={center.id} />
          )}

          {tab === 'integraciones' && (
            <CenterDrawerIntegracionesTab centerId={center.id} canMutate={isSuper} />
          )}

          {tab === 'operacion' && (
            <>
              <div className="field">
                <label className="field-label">Orígenes permitidos (widget) <Req /></label>
                <textarea
                  className="input mono"
                  rows={3}
                  style={{ resize: 'none', fontFamily: 'var(--font-mono)', fontSize: 12 }}
                  placeholder={'https://tu-web.com\nhttps://otro-dominio.com'}
                  value={allowedOrigins}
                  onChange={(e) => { setAllowedOrigins(e.target.value); }}
                />
                <p className="field-help">Una URL por línea. El widget solo funciona desde estos dominios.</p>
              </div>

              <WidgetEmbedSnippet slug={center.slug} />

              <div className="field">
                <label className="field-label">Modo de confirmación</label>
                <select className="select" value={confirmationMode} onChange={(e) => { setConfirmationMode(e.target.value as 'auto' | 'manual'); }}>
                  <option value="auto">Automático — el cliente confirma por email</option>
                  <option value="manual">Manual — el administrador aprueba cada reserva</option>
                </select>
                <p className="field-help">
                  En modo manual, tras la confirmación del cliente la reserva queda pendiente de aprobación.
                </p>
              </div>
              <div className="field">
                <label className="field-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={bhEnabled}
                    onChange={(e) => { setBhEnabled(e.target.checked); }}
                    style={{ width: 14, height: 14, accentColor: 'var(--accent)' }}
                  />
                  Horario de apertura
                </label>
                <p className="field-help" style={{ marginBottom: bhEnabled ? 8 : 0 }}>
                  Si está desactivado, el centro aparece disponible todo el día.
                </p>
                {bhEnabled && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <tbody>
                      {DAY_NAMES.map((dayName, i) => (
                        <tr key={dayName} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--border)' }}>
                          <td style={{ padding: '5px 0', width: 90, color: 'var(--fg-soft)', fontWeight: 500 }}>{dayName}</td>
                          <td style={{ padding: '5px 0', width: 88 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 12 }}>
                              <input
                                type="checkbox"
                                checked={bhDays[i].closed}
                                onChange={(e) => {
                                  setBhDays((prev) => prev.map((d, j) => j === i ? { ...d, closed: e.target.checked } : d));
                                }}
                                style={{ width: 13, height: 13, accentColor: 'var(--accent)' }}
                              />
                              Cerrado
                            </label>
                          </td>
                          <td style={{ padding: '5px 0' }}>
                            {!bhDays[i].closed && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <input
                                  type="time"
                                  className="input mono"
                                  value={bhDays[i].open}
                                  onChange={(e) => {
                                    setBhDays((prev) => prev.map((d, j) => j === i ? { ...d, open: e.target.value } : d));
                                  }}
                                  style={{ width: 90, padding: '3px 6px', fontSize: 12 }}
                                />
                                <span style={{ color: 'var(--fg-muted)' }}>—</span>
                                <input
                                  type="time"
                                  className="input mono"
                                  value={bhDays[i].close}
                                  onChange={(e) => {
                                    setBhDays((prev) => prev.map((d, j) => j === i ? { ...d, close: e.target.value } : d));
                                  }}
                                  style={{ width: 90, padding: '3px 6px', fontSize: 12 }}
                                />
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <LifecycleFields
                pendingTtlHours={pendingTtlHours}
                setPendingTtlHours={setPendingTtlHours}
                approvalTtlHours={approvalTtlHours}
                setApprovalTtlHours={setApprovalTtlHours}
                checkinCompletionBufferMinutes={checkinCompletionBufferMinutes}
                setCheckinCompletionBufferMinutes={setCheckinCompletionBufferMinutes}
              />
            </>
          )}
        </form>
        <div className="modal-foot">
          {onDelete && (
            <button
              type="button"
              className="btn"
              data-variant="danger"
              onClick={onDelete}
              style={{ marginRight: 'auto' }}
            >
              Eliminar centro
            </button>
          )}
          <button className="btn" onClick={onClose}>Cancelar</button>
          {isSuper && tab !== 'salas' && tab !== 'integraciones' && (
            <button className="btn" data-variant="primary" disabled={isSaving} onClick={() => void handleSubmit()}>
              {isSaving ? 'Guardando…' : 'Guardar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tiny presentational helpers, local to this file.
// ---------------------------------------------------------------------------

/**
 * Quick widget actions:
 *  - "Abrir widget" — opens the public widget URL in a new tab so the admin
 *    can preview what the customer sees (no manual URL typing).
 *  - "Código para incrustar" — collapsible section that reveals the iframe
 *    snippet on demand. Hidden by default to keep the Operación tab compact;
 *    the snippet has no value until the admin is ready to copy it to their
 *    site. Origin derived from `window.location.origin` so dev/staging/prod
 *    each surface the right URL without an env round-trip.
 */
function WidgetEmbedSnippet({ slug }: { slug: string }) {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const widgetUrl = `${origin}/widget/${slug}`;
  const snippet = `<iframe
  src="${widgetUrl}"
  width="100%"
  height="720"
  style="border:0;max-width:640px"
  title="Reservas online"
></iframe>`;
  const handleCopy = () => {
    void navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true);
      setTimeout(() => { setCopied(false); }, 1500);
    });
  };
  return (
    <div className="field">
      <label className="field-label">Widget público</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <a
          className="btn"
          href={widgetUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Icons.Globe size={14} />
          Abrir widget
        </a>
        <button
          type="button"
          className="btn"
          data-variant="ghost"
          aria-expanded={open}
          onClick={() => { setOpen((v) => !v); }}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Icons.ChevR
            size={14}
            style={{ transform: open ? 'rotate(90deg)' : undefined, transition: 'transform 0.15s' }}
          />
          Código para incrustar
        </button>
      </div>
      {open && (
        <div style={{ marginTop: 10 }}>
          <div style={{ position: 'relative' }}>
            <textarea
              className="input mono"
              rows={6}
              readOnly
              value={snippet}
              style={{ resize: 'none', fontFamily: 'var(--font-mono)', fontSize: 12, paddingRight: 78 }}
              onFocus={(e) => { e.currentTarget.select(); }}
            />
            <button
              type="button"
              className="btn"
              data-size="xs"
              onClick={handleCopy}
              style={{ position: 'absolute', top: 6, right: 6 }}
            >
              {copied ? 'Copiado' : 'Copiar'}
            </button>
          </div>
          <p className="field-help">
            Pega este código en tu web. El dominio donde lo embebas debe estar listado en{' '}
            <em>Orígenes permitidos</em>.
          </p>
        </div>
      )}
    </div>
  );
}

function Req() {
  return <span style={{ color: 'var(--accent)' }}>*</span>;
}

function FormError({ msg }: { msg: string }) {
  return (
    <div style={{
      padding: '10px 14px',
      background: 'var(--danger-soft)',
      border: '1px solid oklch(0.85 0.05 25)',
      borderRadius: 'var(--radius-sm)',
      fontSize: 13,
      color: 'var(--accent-dark)',
      marginBottom: 12,
    }}>
      {msg}
    </div>
  );
}

function TimezoneSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <select className="select" value={value} onChange={(e) => { onChange(e.target.value); }}>
      <option value="">— Selecciona zona horaria —</option>
      {TIMEZONE_OPTIONS.map((grp) => (
        <optgroup key={grp.group} label={grp.group}>
          {grp.options.map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}
